import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { CreditCard, Check, Loader2, AlertCircle, Sparkles } from 'lucide-react';
interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  stripeUrl: string;
  popular?: boolean;
  discount?: string;
}
// Real Stripe payment links
const packages: CreditPackage[] = [{
  id: 'starter',
  name: 'Starter Pack',
  credits: 5,
  price: 1.99,
  stripeUrl: 'https://buy.stripe.com/4gMcN42NS6LFc3Ebl46Vq01'
}, {
  id: 'pro',
  name: 'Pro Pack',
  credits: 10,
  price: 4.75,
  stripeUrl: 'https://buy.stripe.com/bJe4gyewA3zt7No1Ku6Vq00',
  popular: true,
  discount: 'Best Value'
}, {
  id: 'unlimited',
  name: 'Unlimited Plan',
  credits: -1,
  price: 6.75,
  stripeUrl: 'https://buy.stripe.com/6oUeVcgEIfib3x84WG6Vq02',
  discount: 'Monthly'
}];
interface CreditPurchaseProps {
  currentCredits?: number;
  onPurchaseComplete?: () => void;
}
export function CreditPurchase({
  currentCredits = 0,
  onPurchaseComplete
}: CreditPurchaseProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Check for success/cancel in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      // Show success message
      alert('Payment successful! Your credits have been added.');
      // Remove query params
      window.history.replaceState({}, '', window.location.pathname);
      // Reload credits
      if (onPurchaseComplete) {
        onPurchaseComplete();
      }
    }
    if (params.get('canceled') === 'true') {
      // Show canceled message
      setError('Payment was canceled.');
      // Remove query params
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [onPurchaseComplete]);
  const handlePurchase = async (pkg: CreditPackage) => {
    setLoading(pkg.id);
    setError(null);
    try {
      // Redirect directly to Stripe Checkout
      window.location.href = pkg.stripeUrl;
    } catch (err) {
      console.error('Error redirecting to checkout:', err);
      setError('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };
  return <div className="space-y-6" data-id="element-535">
      {/* Current Balance */}
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4" data-id="element-536">
        <div className="flex items-center justify-between" data-id="element-537">
          <div data-id="element-538">
            <p className="text-sm text-muted-foreground mb-1" data-id="element-539">
              Current Balance
            </p>
            <p className="text-3xl font-bold" data-id="element-540">{currentCredits} Credits</p>
          </div>
          <Sparkles size={32} className="text-primary" data-id="element-541" />
        </div>
      </div>

      {/* Error Message */}
      {error && <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-start gap-3" data-id="element-542">
          <AlertCircle size={20} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-543" />
          <div data-id="element-544">
            <p className="text-sm font-medium text-destructive mb-1" data-id="element-545">
              Payment Error
            </p>
            <p className="text-sm text-muted-foreground" data-id="element-546">{error}</p>
          </div>
        </div>}

      {/* Packages */}
      <div data-id="element-547">
        <h3 className="text-lg font-semibold mb-2" data-id="element-548">Purchase Credits</h3>
        <p className="text-sm text-muted-foreground mb-4" data-id="element-549">
          Credits are used for comment analysis. Each credit covers 100
          comments.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-550">
          {packages.map(pkg => <Card key={pkg.id} className={pkg.popular ? 'border-primary shadow-lg relative' : 'relative'} data-id="element-551">
              {pkg.popular && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2" data-id="element-552">
                  <div className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full" data-id="element-553">
                    Most Popular
                  </div>
                </div>}

              <CardContent className="p-6" data-id="element-554">
                <h4 className="font-semibold text-lg mb-2" data-id="element-555">{pkg.name}</h4>

                {pkg.discount && <div className="text-xs text-green-600 dark:text-green-400 font-medium mb-2" data-id="element-556">
                    {pkg.discount}
                  </div>}

                <div className="mb-4" data-id="element-557">
                  <span className="text-3xl font-bold" data-id="element-558">${pkg.price}</span>
                  {pkg.credits === -1 ? <span className="text-muted-foreground ml-2 text-sm" data-id="element-559">
                      /month
                    </span> : <span className="text-muted-foreground ml-2 text-sm" data-id="element-560">
                      ${(pkg.price / pkg.credits).toFixed(2)}/credit
                    </span>}
                </div>

                <ul className="space-y-2 mb-6" data-id="element-561">
                  {pkg.credits === -1 ? <>
                      <li className="flex items-center text-sm" data-id="element-562">
                        <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-563" />
                        Unlimited transcripts
                      </li>
                      <li className="flex items-center text-sm" data-id="element-564">
                        <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-565" />
                        Unlimited comments
                      </li>
                      <li className="flex items-center text-sm" data-id="element-566">
                        <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-567" />
                        Cancel anytime
                      </li>
                      <li className="flex items-center text-sm" data-id="element-568">
                        <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-569" />
                        Priority support
                      </li>
                    </> : <>
                      <li className="flex items-center text-sm" data-id="element-570">
                        <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-571" />
                        {pkg.credits} credits
                      </li>
                      <li className="flex items-center text-sm" data-id="element-572">
                        <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-573" />
                        ~{(pkg.credits * 100).toLocaleString()} comments
                      </li>
                      <li className="flex items-center text-sm" data-id="element-574">
                        <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-575" />
                        Never expires
                      </li>
                      <li className="flex items-center text-sm" data-id="element-576">
                        <Check size={16} className="text-green-500 mr-2 flex-shrink-0" data-id="element-577" />
                        Instant delivery
                      </li>
                    </>}
                </ul>

                <Button onClick={() => handlePurchase(pkg)} disabled={loading !== null} className="w-full" variant={pkg.popular ? 'default' : 'outline'} data-id="element-578">
                  {loading === pkg.id ? <>
                      <Loader2 size={16} className="mr-2 animate-spin" data-id="element-579" />
                      Redirecting...
                    </> : <>
                      <CreditCard size={16} className="mr-2" data-id="element-580" />
                      {pkg.credits === -1 ? 'Subscribe' : 'Buy Now'}
                    </>}
                </Button>
              </CardContent>
            </Card>)}
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-secondary/20 p-4 rounded-lg" data-id="element-581">
        <h4 className="font-medium text-sm mb-2 flex items-center gap-2" data-id="element-582">
          <CreditCard size={16} className="text-primary" data-id="element-583" />
          Secure Payment
        </h4>
        <p className="text-xs text-muted-foreground mb-2" data-id="element-584">
          All payments are processed securely through Stripe. We never store
          your credit card information.
        </p>
        <p className="text-xs text-muted-foreground" data-id="element-585">
          💡 Credits never expire and can be used anytime for comment analysis.
        </p>
      </div>

      {/* How Credits Work */}
      <div className="border border-border rounded-lg p-4" data-id="element-586">
        <h4 className="font-medium text-sm mb-3" data-id="element-587">How Credits Work</h4>
        <div className="space-y-2 text-xs text-muted-foreground" data-id="element-588">
          <p data-id="element-589">• 1 credit = 100 comments analyzed</p>
          <p data-id="element-590">• Average video (200-500 comments) = 2-5 credits</p>
          <p data-id="element-591">• Viral video (1000+ comments) = 10+ credits</p>
          <p data-id="element-592">• Credits are deducted only after successful analysis</p>
        </div>
      </div>
    </div>;
}